import _ from 'lodash';

import SharedConfig from 'shared/config';

import GameData from 'shared/gameData';

import { getEnchantByTokenizedName } from 'shared/enchants';
import { SOCKET_TYPES } from 'shared/constants';
import {
  defaultSlot,
  getWeaponHands,
  getEmbellishment,
  CRAFTED_STAT_COMBOS,
} from './items';
import {
  getGemDetails,
} from './gems';

import {
  MAX_LEVEL,
  activeLoadout,
  classes,
  races,
  factions,
  specMap,
  isTank,
  getSpecInfo,
} from './character';

import {
  fightStyles,
  getIterations,
  isSmartIterations,
  expansionOptions,
} from './simcOptions';

import { find as findRealm } from './realms';

import TalentTraits from './talentTraits';
import ItemCache from './itemCache';

import { capitalizeName } from './string';

const hasGet = (obj, prop, defaultValue) => (_.has(obj, prop) ? _.get(obj, prop) : defaultValue);

const TOKEN_EQUALS = '=';
const TOKEN_COMMENT = '#';
const TOKEN_QUOTE = '"';

const PROFILESET_COMMAND_REGEX = /^profileset\.(.*)$/;

const COMMANDS_CLASSES = [
  // classes
  'deathknight',
  'demonhunter',
  'druid',
  'evoker',
  'hunter',
  'mage',
  'monk',
  'priest',
  'paladin',
  'rogue',
  'shaman',
  'warlock',
  'warrior',
];

const COMMANDS_GENERAL = [
  'level',
  'race',
  'region',
  'server',
  'role',
  'position',
  'professions',
  'talents',
  'class_talents',
  'spec_talents',
  'spec',
  'thumbnail',

  // allow these
  'artifact',
  'crucible',
  'zandalari_loa',

  'azerite_essences',
  'covenant',
  'soulbind',
  'renown',
];

const COMMANDS_GEAR = [
  'head',
  'neck',
  'shoulder',
  'back',
  'chest',
  'shirt',
  'tabard',
  'wrist',
  'hands',
  'waist',
  'legs',
  'feet',
  'finger1',
  'finger2',
  'trinket1',
  'trinket2',
  'main_hand',
  'off_hand',
];

export const COMMANDS_GEAR_ALIASES = {
  shoulders: 'shoulder',
  wrists: 'wrist',
  leg: 'legs',
  foot: 'feet',
  hand: 'hands',
  ring1: 'finger1',
  ring2: 'finger2',
};

const COMMANDS_GEAR_OPTIONAL = [
  'shirt',
  'tabard',
  'off_hand',
];

const COMMANDS_GEAR_REQUIRED = _.reject(COMMANDS_GEAR, slot => _.includes(COMMANDS_GEAR_OPTIONAL, slot));

const COMMANDS_MISC = [
  'armory',
  'local_json',
];

// used when converting copy input to profileset input
const COMMANDS_PLAYER_SCOPE = _.union(
  COMMANDS_GEAR,
  _.keys(COMMANDS_GEAR_ALIASES),
  [
    // sc_player.cpp
    'name',
    'value',
    'op',
    'default',
    'delay',
    'condition',
    'value_else',
    'name',
    'mana',
    'sec',
    'name',
    'slot',
    'slots',
    'name',
    'name',
    'random',
    'wait',
    'for_next',
    'resource',
    'extra_amount',
    'name',
    'origin',
    'region',
    'server',
    'thumbnail',
    'id',
    'talents',
    'talent_override',
    'class_talents',
    'spec_talents',
    'race',
    'timeofday',
    'level',
    'ready_trigger',
    'role',
    'target',
    'skill',
    'distance',
    'position',
    'professions',
    'actions',
    'actions',
    'actions',
    'action_list',
    'sleeping',
    'quiet',
    'save',
    'save_gear',
    'save_talents',
    'save_actions',
    'comment',
    'bugs',
    'world_lag',
    'world_lag_stddev',
    'brain_lag',
    'brain_lag_stddev',
    'cooldown_tolerance',
    'scale_player',
    'tmi_output',
    'tmi_window',
    'spec',
    'specialization',
    'stat_timelines',
    'disable_hotfixes',
    'min_gcd',
    'potion',
    'flask',
    'food',
    'augmentation',
    'x_pos',
    'y_pos',
    'meta_gem',
    'items',
    'items',
    'head',
    'neck',
    'shoulders',
    'shoulder',
    'shirt',
    'chest',
    'waist',
    'legs',
    'leg',
    'feet',
    'foot',
    'wrists',
    'wrist',
    'hands',
    'hand',
    'finger1',
    'finger2',
    'ring1',
    'ring2',
    'trinket1',
    'trinket2',
    'back',
    'main_hand',
    'off_hand',
    'tabard',
    'set_bonus',
    'gear_strength',
    'gear_agility',
    'gear_stamina',
    'gear_intellect',
    'gear_spirit',
    'gear_spell_power',
    'gear_attack_power',
    'gear_expertise_rating',
    'gear_haste_rating',
    'gear_hit_rating',
    'gear_crit_rating',
    'gear_parry_rating',
    'gear_dodge_rating',
    'gear_health',
    'gear_mana',
    'gear_rage',
    'gear_energy',
    'gear_focus',
    'gear_runic',
    'gear_armor',
    'gear_mastery_rating',
    'gear_versatility_rating',
    'gear_bonus_armor',
    'gear_leech_rating',
    'gear_run_speed_rating',
    'enchant_strength',
    'enchant_agility',
    'enchant_stamina',
    'enchant_intellect',
    'enchant_spirit',
    'enchant_spell_power',
    'enchant_attack_power',
    'enchant_expertise_rating',
    'enchant_armor',
    'enchant_haste_rating',
    'enchant_hit_rating',
    'enchant_crit_rating',
    'enchant_mastery_rating',
    'enchant_versatility_rating',
    'enchant_bonus_armor',
    'enchant_leech_rating',
    'enchant_run_speed_rating',
    'enchant_health',
    'enchant_mana',
    'enchant_rage',
    'enchant_energy',
    'enchant_focus',
    'enchant_runic',
    'infinite_energy',
    'infinite_focus',
    'infinite_health',
    'infinite_mana',
    'infinite_rage',
    'infinite_runic',
    'infinite_astral_power',
    'initial_resource',
    'skip_actions',
    'modify_action',
    'use_apl',
    'reaction_time_mean',
    'reaction_time_stddev',
    'reaction_time_nu',
    'reaction_time_offset',
    'reaction_time_max',
    'stat_cache',
    'karazhan_trinkets_paired',
    'azerite_override',
    'artifact',
    'crucible',
    'artifact_override',
    'disable_artifact',

    // class modules
    'sync_weapons',
    'interval',
    'interval_stddev',
    'damage',
    'fallen_crusader_str',
    'fallen_crusader_rppm',
    'targets',
    'firestarter_time',
    'blessing_of_wisdom_count',
    'allow_shimmer_lance',
    'target_self',
    'max_energy',
    'predator_rppm',
    'feral_t21_2pc',
    'feral_t21_4pc',
    'initial_astral_power',
    'initial_moon_stage',
    'lively_spirit_stacks',
    'outside',
    'catweave_bear',
    'type',
    'mode',
    'animation_cancel',
    'target_reach',
    'initial_fury',
    'soul_shards',
    'default_pet',
    'allow_sephuz',
    'sync_weapons',
    'swing_timer_variance',
    'stormlash_targets',
    'raptor_glyph',
    'strikes',
    'paladin_fake_sov',
    'damage',
    'name',
    'precast_time',
    'summon_pet',
    'hunter.pet_attack_speed',
    'hunter_fixed_time',
    'lethal',
    'nonlethal',
    'sync_weapons',
    'precombat_seconds',
    'precombat_seconds',
    'precombat_seconds',
    'precombat_seconds',
    'off_gcd',
    'precombat_seconds',
    'off_gcd',
    'precombat_seconds',
    'slot',
    'swap_to',
    'off_hand_secondary',
    'main_hand_secondary',
    'initial_combo_points',
    'fixed_rtb',
    'fixed_rtb_odds',
    'ssw_refund_offset',
    'fok_rotation',
    'rogue_optimize_expressions',
    'rogue_ready_trigger',
    'sync_weapons',
    'interval',
    'interval_stddev',
    'pct_health',
    'initial_chi',
    'disable_heroic_leap',
    'toggle',
    'non_dps_mechanics',
    'warrior_fixed_time',
    'into_the_fray_friends',
    'ignore_debuff',
    'double_dot',
    'autounshift',
    'priest.autounshift',
    'priest_fixed_time',
    'priest.fixed_time',
    'priest_ignore_healing',
    'priest.ignore_healing',
    'priest_suppress_sephuz',
    'priest.suppress_sephuz',
    'zandalari_loa',
  ],
);

export const COMMANDS_APL = [
  /^actions(\.[a-z0-9_]+)?$/,
  /^apl_variable\.[a-z0-9_]+?$/,

  // general
  'bugs',
  'timewalk',
  'scale_to_itemlevel',
  'timeofday',
  'auto_attacks_always_land',
  'pvp',
  'distance',

  'target_race',

  'rotation',
  'skip_actions',
  'disable_azerite',
  'target_level',

  /^external_buffs\..*$/,

  'no_cds',

  'bloodlust_time',
  'bloodlust_percent',

  'initial_resource',

  // UI option overrides
  // Useful to set character specific options
  'potion',
  'flask',
  'food',
  'augmentation',
  'temporary_enchant',

  // The War Within

  /^thewarwithin\..*$/,
  'earthen_mineral',

  // Dragonflight

  /^dragonflight\..*$/,

  // Shadowlands options

  // huh, I can just allow all "shadowlands." options, can't i?
  /^shadowlands\..*$/,

  // BfA options
  /^bfa\..*$/,

  // Legion options

  /^legion\..*$/,

  // Class specific options

  /^deathknight\..*$/,
  /^demonhunter\..*$/,
  /^druid\..*$/,
  /^evoker\..*$/,

  // hunter
  'summon_pet',
  /^hunter\..*$/,

  // mage
  'allow_shimmer_lance',
  'firestarter_time',
  'blessing_of_wisdom_count',
  'global_cinder_count',
  'scorch_delay',
  'frozen_duration',
  'focus_magic_interval',
  'focus_magic_stddev',
  'focus_magic_crit_chance',
  'arcane_missiles_chain_stddev',
  'mirrors_of_torment_interval',
  /^mage\..*$/,


  // monk
  'initial_chi',
  /^monk\..*$/,

  // paladin
  'paladin_fixed_holy_wrath_health_pct',
  /^paladin\..*$/,

  // priest
  /^priest\..*$/,

  // rogue
  'initial_combo_points',
  'fixed_rtb',
  'fixed_rtb_odds',
  'ssw_refund_offset',
  'fok_rotation',
  'priority_rotation',
  /^rogue\..*$/,

  // shaman
  'stormlash_targets',
  /^shaman\..*$/,

  // warlock
  'use_pre_soulburn',
  'dark_intent_target',
  'flashpoint_threshold',
  'default_pet',
  'soul_shards',
  /^warlock\..*$/,

  /^warrior\..*$/,

  // infinite resource
  'infinite_energy',
  'infinite_focus',
  'infinite_health',
  'infinite_mana',
  'infinite_rage',
  'infinite_runic',
  'infinite_astral_power',

  'load_default_talents',

  // Blizzard APL
  'use_blizzard_action_list',
  'one_button_mode',
  'use_cds_with_blizzard_action_list',
  'enable_spell_queue',
];

const COMMANDS_ADVANCED_INVALID = [
  // general
  'process_priority',
  'threads',
  'spell_query_xml_output_file',
  'item_db_source',
  'proxy',

  // output
  'save_profiles',
  // 'debug', // try allowing this
  'debug_each',
  'html',
  'json',
  'json2',
  'hosted_html',
  'xml',
  'xml_style',
  // 'log', // try allowing this
  'output',
  'save_raid_summary',
  'save_gear_comments',

  'guild',
  'local_json',
  'http_clear_cache',
  'cache_items',
  'cache_players',
  'save_prefix',
  'save_suffix',
  'save_talent_str',

  'reforge_plot_output_file',
  'monitor_cpu',
  'apikey',

  'input',
  'save',
  'save_gear',
  'save_talents',
  'save_actions',
  'tmi_output',
];

const TEMPLATE_VARIABLE_PATTERN = /\$\(/;

const COMMANDS_VALID = [
  ...COMMANDS_CLASSES,
  ...COMMANDS_GENERAL,
  ...COMMANDS_GEAR,
  ...COMMANDS_MISC,
];

const COMMANDS_ACTORS = [
  ...COMMANDS_CLASSES,
  'copy',
  'armory',
  'local_json',
];

const int = i => parseInt(i, 10);

const ITEM_TO_SIMC_SLOT_MAP = {
  mainHand: 'main_hand',
  offHand: 'off_hand',
};
const simcSlot = slot => ITEM_TO_SIMC_SLOT_MAP[slot] ?? slot;

function parseLine(rawLine) {
  const line = _.trim(rawLine);
  if (line === '') {
    // empty line
    return null;
  } if (_.startsWith(line, TOKEN_COMMENT)) {
    // comment, remove the first comment token
    return { comment: rawLine.replace(TOKEN_COMMENT, '') };
  }

  // normal line
  const equalsPos = line.indexOf(TOKEN_EQUALS);
  let key;
  let value;
  let op;
  let quoted;
  if (equalsPos === -1) {
    // if no =, treat the entire line as the key
    key = line;
    value = '';
  } else {
    key = line.substring(0, equalsPos);
    // handle += operator in a quick/fake way
    if (_.endsWith(key, '+')) {
      key = key.substring(0, key.length - 1);
      op = '+=';
    }
    value = line.substring(equalsPos + 1);
  }
  // remove quotes
  if (_.startsWith(value, TOKEN_QUOTE) && _.endsWith(value, TOKEN_QUOTE)) {
    value = value.substring(1, value.length - 1);
    quoted = true;
  }
  // resolve command aliases
  key = COMMANDS_GEAR_ALIASES[key] || key;
  return { key, value, op, quoted };
}
function parse(input) {
  return _.split(input, '\n').reduce((result, rawLine) => {
    const parsedLine = parseLine(rawLine);
    if (parsedLine) {
      if (parsedLine.comment) {
        parsedLine.commentParsed = parseLine(parsedLine.comment);
      }
      result.push(parsedLine);
    } else {
      result.push({ emptyLine: true });
    }
    return result;
  }, []);
}

function stringify(data) {
  let output = '';
  _.each(data, (item) => {
    if (item) {
      if (item.emptyLine) {
        output += '\n';
      } else if (item && _.has(item, 'comment')) {
        output += `#${item.comment}\n`;
      } else {
        let value = item.value;
        if (item.quoted || _.includes(value, ' ')) {
          value = `"${value}"`;
        }
        if (item.op) {
          output += `${item.key}${item.op}${value}\n`;
        } else if (item.key) {
          output += `${item.key}=${value}\n`;
        }
      }
    }
  });

  // remove a trailing newline
  output = _.trim(output);

  return output;
}

function isComment(item) {
  return _.has(item, 'comment');
}
function getComments(data) {
  return _.filter(data, isComment);
}

const find = (data, key, searchComments = false) => {
  const item = _.find(data, { key });
  if (item) {
    return item.value;
  }
  if (searchComments) {
    const commentItem = _.find(data, { commentParsed: { key } });
    return _.get(commentItem, 'commentParsed.value', null);
  }
  return null;
};

const reject = (data, key) => _.reject(data, { key });

function set(data, key, value) {
  // TODO: check for dupes, throw if dupes
  const idx = _.findIndex(data, { key });
  if (idx >= 0) {
    return [
      ...data.slice(0, idx),
      { key, value },
      ...data.slice(idx + 1),
    ];
  }

  return [
    ...data,
    { key, value },
  ];
}

/* eslint-disable no-param-reassign */
function impureSet(data, key, value) {
  const idx = _.findIndex(data, { key });

  if (idx >= 0) {
    data[idx].value = value;
  } else {
    data.push({ key, value });
  }
}
/* eslint-enable no-param-reassign */

function prepend(data, key, value) {
  return [
    { key, value },
    ...data,
  ];
}

function append(data, key, value) {
  return [
    ...data,
    { key, value },
  ];
}

function appendMany(data, appends) {
  const toAdd = _.map(appends, ([key, value]) => ({ key, value }));
  return [
    ...data,
    ...toAdd,
  ];
}

function profilesetAppend(data, profileset, key, value) {
  return [
    ...data,
    {
      key: `profileset."${profileset}"`,
      value: `${key}=${value}`,
      op: '+=',
    },
  ];
}

/* eslint-disable no-param-reassign */
function impureProfilesetAppend(data, profileset, key, value) {
  data.push({
    key: `profileset."${profileset}"`,
    value: `${key}=${value}`,
    op: '+=',
  });
}
/* eslint-enable no-param-reassign */

function isEmptyLine(data) {
  return _.get(data, 'emptyLine', false);
}

function prependEmptyLine(data) {
  return [
    { emptyLine: true },
    ...data,
  ];
}
function appendEmptyLine(data) {
  return [
    ...data,
    { emptyLine: true },
  ];
}

function prependComment(data, comment) {
  return [
    { comment },
    ...data,
  ];
}

function appendComment(data, comment) {
  return [
    ...data,
    { comment },
  ];
}

function appendAfter(data, key, value, afterKey) {
  const idx = _.findIndex(data, { key: afterKey });
  return [
    ...data.slice(0, idx + 1),
    { key, value },
    ...data.slice(idx + 1),
  ];
}

function setMany(data, pairs) {
  let result = data;
  pairs.forEach((pair) => {
    if (pair[1] !== null) {
      result = set(result, pair[0], pair[1]);
    }
  });
  return result;
}

function removeComments(data) {
  return _.reject(data, isComment);
}
function removeEmptyLines(data) {
  return _.reject(data, isEmptyLine);
}
function isValidCommand(item) {
  return isComment(item) || isEmptyLine(item) || _.includes(COMMANDS_VALID, item.key);
}
function isActorCommand(item) {
  return _.includes(COMMANDS_ACTORS, item.key);
}

function getValidCommands(data) {
  return _.filter(data, isValidCommand);
}
function getInvalidCommands(data) {
  return _.reject(data, isValidCommand);
}

function isValidAplCommand(item) {
  if (isComment(item) || isEmptyLine(item)) {
    return true;
  }
  let valid = false;
  _.each(COMMANDS_APL, validCommand => {
    if (_.isRegExp(validCommand)) {
      if (item.key.match(validCommand)) {
        valid = true;
      }
    } else if (item.key === validCommand) {
      valid = true;
    }
    // keep iterating until valid command is found
    return !valid;
  });
  return valid;
}

function getInvalidAplCommands(data) {
  return _.reject(data, isValidAplCommand);
}

function isProfilesetCommand(item) {
  return item?.key && item.key.match && item.key.match(PROFILESET_COMMAND_REGEX);
}

function getProfilesetCommands(data) {
  return _.filter(data, isProfilesetCommand);
}

function getNonProfilesetCommands(data) {
  return _.reject(data, isProfilesetCommand);
}

function getProfilesetCommand(item) {
  if (isProfilesetCommand(item)) {
    const command = parse(item.value);
    return command[0];
  }
  return null;
}

function getProfilesetName(item) {
  if (item && item.key) {
    const matches = item.key.match(/profileset\."?([^"]+)"?/);
    if (matches) {
      return matches[1];
    }
  }
  return null;
}
function getProfilesetNames(data) {
  const profilesetCommands = _.filter(data, isProfilesetCommand);
  const names = _.map(profilesetCommands, getProfilesetName);
  return _.uniq(names);
}

function getProfilesets(data) {
  const profilesetCommands = _.filter(data, isProfilesetCommand);
  const profilesetGroups = _.groupBy(profilesetCommands, 'key');
  return profilesetGroups;
}
function profilesetRemove(data, profilesetName) {
  return _.reject(data, item => isProfilesetCommand(item) && getProfilesetName(item) === profilesetName);
}
function removeProfilesets(data, names) {
  return _.reject(data, line => names.has(getProfilesetName(line)));
}
function impureRemoveProfilesets(data, names) {
  let i = data.length;
  // eslint-disable-next-line no-plusplus
  while (i--) {
    if (names.has(getProfilesetName(data[i]))) {
      data.splice(i, 1);
    }
  }
}

function isInvalidAdvancedCommand(item) {
  let command = item;
  if (isProfilesetCommand(item)) {
    command = getProfilesetCommand(item);
  }
  return _.includes(COMMANDS_ADVANCED_INVALID, command.key) ||
    (command.key && command.key.match(TEMPLATE_VARIABLE_PATTERN));
}

function getInvalidAdvancedCommands(data) {
  return _.map(
    _.filter(data, isInvalidAdvancedCommand),
    command => {
      if (isProfilesetCommand(command)) {
        const parsed = parse(command.value);
        return parsed[0];
      }
      return command;
    },
  );
}

function getActors(data) {
  return _.filter(data, isActorCommand);
}


function isGearCommand(item) {
  return _.includes(COMMANDS_GEAR, item.key);
}
function isRequiredGearCommand(item) {
  return _.includes(COMMANDS_GEAR_REQUIRED, item.key);
}
function getGear(data, searchComments = false) {
  const equipped = _.filter(data, isGearCommand);
  if (!searchComments) {
    return equipped;
  }

  return [
    ...equipped,
    ..._.map(_.filter(data, item => item && item.commentParsed && isGearCommand(item.commentParsed)), 'commentParsed'),
  ];
}
function getRequiredGear(data) {
  return _.filter(data, isRequiredGearCommand);
}

function isCompleteGearSet(data) {
  const gearCommands = getRequiredGear(data);
  const requiredSlots = _.map(
    COMMANDS_GEAR_REQUIRED,
    slot => ({
      slot,
      filled: _.find(gearCommands, { key: slot }) !== undefined,
    }),
  );
  return _.filter(requiredSlots, { filled: false }).length === 0;
}

function getGearFromComments(simcData) {
  const comments = _.filter(
    _.map(
      _.filter(simcData, isComment),
      comment => parseLine(comment.comment),
    ),
    item => item && isGearCommand(item),
  );

  return comments;
}

const talentKeys = [
  'talents',
  'class_talents',
];

function getTalentLoadouts(simcData) {
  const loadouts = [];
  let loadoutName = null;
  let index = 0;
  _.each(simcData, entry => {
    let matches;
    let loadout;
    if (_.includes(talentKeys, entry.key)) {
      loadout = {
        name: 'Active',
        rawString: entry.value,
        active: true,
      };
      index += 1;
    // eslint-disable-next-line no-cond-assign
    } else if (matches = entry.comment?.match(/ Saved Loadout: (.*)/)) {
      loadoutName = matches[1];
    } else if (loadoutName && _.includes(talentKeys, entry.commentParsed?.key)) {
      loadout = {
        name: loadoutName,
        rawString: entry.commentParsed.value,
        active: false,
      };
      loadoutName = null;
    }

    if (loadout) {
      const talents = TalentTraits.parse(loadout.rawString);
      loadouts.push({
        index,
        ...loadout,
        // do a combo parse/stringify so that the string will always be normalized to the entry.id order
        talents,
        string: TalentTraits.stringify(talents),
      });
      index += 1;
    }
  });
  return loadouts;
}


function getCharacter(simcData) {
  const region = find(simcData, 'region');
  const realm = find(simcData, 'server');
  const spec = find(simcData, 'spec');
  const actors = getActors(simcData);

  let character;
  let charClass;
  if (actors.length > 0) {
    character = actors[0].value;
    charClass = actors[0].key.toLowerCase();
  } else {
    throw new Error('Unable to find primary actor');
  }

  return {
    region,
    realm,
    character,
    spec,
    charClass,
  };
}


const ATTRIBUTES_TO_KEEP = [
  'id',
  'bonus_id',
  'gem_id',
  'enchant_id',
  'gems',
  'enchant',
  'drop_level',
  'ilevel',
  'crafted_stats',
  'titan_disc_id',
];

const makeAttrs = item => {
  const attrBits = _.pickBy(item, (value, key) => _.includes(ATTRIBUTES_TO_KEEP, key) && value);
  const attrStrings = _.map(attrBits, (value, key) => `${key}=${value}`);
  return `,${attrStrings.join(',')}`;
};

const OPTIMIZE_SLOTS = [
  'head', 'neck', 'shoulder', 'back', 'chest', 'wrist', 'hands',
  'waist', 'legs', 'feet', 'finger1', 'finger2', 'trinket1', 'trinket2',
  'main_hand', 'off_hand',
];
const slotsLen = OPTIMIZE_SLOTS.length;

/* eslint-disable no-plusplus,no-param-reassign*/
function addOptimizeActor(data, combos, comboNum, simMode, primaryActor, dataEnd, verbose) {
  const combo = combos[comboNum];
  const gearset = combo.gear || combo;
  const comboTalents = combo.talents;
  if (simMode === 'copy') {
    if (comboNum !== 0) {
      data[dataEnd++] = { key: 'copy', value: `Combo ${comboNum + 1},${primaryActor}` };
    }
    if (verbose) {
      data[dataEnd++] = {
        comment: `## Combo ${comboNum + 1}`,
      };
    }
    for (let slot = 0; slot < slotsLen; slot += 1) {
      const it = gearset[OPTIMIZE_SLOTS[slot]];
      if (it) {
        if (verbose) {
          data[dataEnd++] = {
            comment: ` ${it.name} ${it.itemLevel}`,
          };
        }
        data[dataEnd++] = {
          key: OPTIMIZE_SLOTS[slot],
          value: makeAttrs(it),
        };
      } else {
        data[dataEnd++] = {
          key: OPTIMIZE_SLOTS[slot],
          value: ',',
        };
      }
    }
    if (comboTalents) {
      data[dataEnd++] = {
        key: 'talents',
        value: comboTalents,
      };
    }
  } else if (simMode === 'profileset') {
    if (comboNum === 0) {
      // base actor
      if (verbose) {
        data[dataEnd++] = {
          comment: `## Combo ${comboNum + 1}`,
        };
      }
      for (let slot = 0; slot < slotsLen; slot += 1) {
        const it = gearset[OPTIMIZE_SLOTS[slot]];
        if (it) {
          if (verbose) {
            data[dataEnd++] = {
              comment: ` ${it.name} ${it.itemLevel}`,
            };
          }
          data[dataEnd++] = {
            key: OPTIMIZE_SLOTS[slot],
            value: makeAttrs(it),
          };
        } else {
          data[dataEnd++] = {
            key: OPTIMIZE_SLOTS[slot],
            value: ',',
          };
        }
      }
      if (comboTalents) {
        data[dataEnd++] = {
          key: 'talents',
          value: comboTalents,
        };
      }
    } else {
      // profile sets
      if (verbose) {
        data[dataEnd++] = {
          comment: `## Combo ${comboNum + 1}`,
        };
      }
      for (let slot = 0; slot < slotsLen; slot += 1) {
        const it = gearset[OPTIMIZE_SLOTS[slot]];
        if (it) {
          if (verbose) {
            data[dataEnd++] = {
              comment: ` ${it.name} ${it.itemLevel}`,
            };
          }
          data[dataEnd++] = {
            key: `profileset."Combo ${comboNum + 1}"`,
            value: `${OPTIMIZE_SLOTS[slot]}=${makeAttrs(it)}`,
            op: '+=',
          };
        } else {
          data[dataEnd++] = {
            key: `profileset."Combo ${comboNum + 1}"`,
            value: `${OPTIMIZE_SLOTS[slot]}=,`,
            op: '+=',
          };
        }
      }
      if (comboTalents) {
        data[dataEnd++] = {
          key: `profileset."Combo ${comboNum + 1}"`,
          value: `talents=${comboTalents}`,
          op: '+=',
        };
      }
    }
  } else {
    throw new Error(`Unknown or unhandled simulation mode "${simMode}" given to formInputToData`);
  }

  data[dataEnd++] = { emptyLine: true };

  return dataEnd;
}
/* eslint-enable no-plusplus */

const ITEM_TO_STRING_ATTRS = [
  'id',
  'enchant_id',
  'bonus_id',
  'ilevel',
  'gem_id',
  'crafted_stats',
  'titan_disc_id',
];
const itemToString = it => {
  const itemAttrs = _.map(
    _.filter(ITEM_TO_STRING_ATTRS, attr => it[attr]),
    (attr) => `${attr}=${it[attr]}`,
  );
  if (it.bonusLists && !it.bonus_id) {
    const bonusIds = _.join(it.bonusLists, '/');
    itemAttrs.push(`bonus_id=${bonusIds}`);
  }
  return _.join(itemAttrs, ',');
};

function formInputToData(formData) {
  const {
    type,
    text,
    armory,
    baseActorName,
    gearsets,
    talents,
    talentSets,
    iterations,
    smartHighPrecision,
    fightStyle,
    fightLength,
    enemyCount,
    bloodlust,
    arcaneIntellect,
    fortitude,
    battleShout,
    mysticTouch,
    chaosBrand,
    skyfury,
    markOfTheWild,
    huntersMark,
    powerInfusion,
    bleeding,
    reportDetails,
    apl,
    character,
    optimize,
    droptimizer,
    droptimizerItems,
    ptr,
    simMode = 'profileset',
    verbose = false,
    expertHeader,
    expertPreActors,
    expertRaidActors,
    expertPostActors,
    expertFooter,
  } = formData;

  let simcData = [];
  let primaryActor;
  let charClass;
  let charSpec;

  const fightStyleInfo = _.find(fightStyles, { value: fightStyle });
  const overrides = _.get(fightStyleInfo, 'overrides');

  if (expertHeader) {
    const headerData = parse(expertHeader);
    simcData = appendComment(simcData, ' SimC Expert Mode: Header');
    _.each(headerData, item => simcData.push(item));
    simcData = appendEmptyLine(simcData);
  }

  simcData = appendComment(simcData, ' Base Actor');

  const optimizeCombos = _.get(optimize, 'combinations', []);

  if (type === 'optimize') {
    //////////////////////////////////////////////////////////////////////////////
    // Top Gear actor
    //////////////////////////////////////////////////////////////////////////////
    const data = optimize && optimize.data;

    const actors = getActors(data);
    if (actors && actors.length > 0) {
      primaryActor = actors[0].value;
      simcData = append(simcData, actors[0].key, actors[0].value);
      charClass = actors[0].key;
    }

    const copyOptions = [
      'race',
      'level',
      'spec',
      'role',
      'position',
      'zandalari_loa',
      'talents',
      'covenant',
      'soulbind',
      'renown',
    ];

    _.each(copyOptions, key => {
      const value = find(data, key);
      if (value) {
        simcData = append(simcData, key, value);
        if (key === 'spec') {
          charSpec = value;
        }
      }
    });

    // make the optimize base actor here
    const baseActor = [];
    addOptimizeActor(baseActor, optimizeCombos, 0, simMode, primaryActor, 0, verbose);
    simcData = simcData.concat(baseActor);
  } else if (text) {
    //////////////////////////////////////////////////////////////////////////////
    // SimC input actor (not top gear)
    //////////////////////////////////////////////////////////////////////////////
    const parsed = parse(text);
    const actors = getActors(parsed);
    if (actors && actors.length > 0) {
      primaryActor = baseActorName || getActors(parsed)[0].value;
      charClass = getActors(parsed)[0].key;
    }
    charSpec = find(parsed, 'spec');

    _.each(parsed, line => {
      simcData.push(line);
    });
  } else if (armory) {
    //////////////////////////////////////////////////////////////////////////////
    // Armory actor
    //////////////////////////////////////////////////////////////////////////////
    const { region, realm } = armory;
    // if the character object is present from the armory API, use that name
    // instead of the form. This handles accented characters that got uppercased
    // by Blizz
    const name = character ? character.name : armory.name;
    simcData = appendMany(simcData, [
      ['armory', [region, realm, name].join(',')],
    ]);
    // adjust name here to make sure name is properly capitalized as it comes
    // back from the armory as that is what simc does
    primaryActor = baseActorName || capitalizeName(name);

    charClass = _.get(character, 'class');

    // apply override talents to base armory actor
    if (talents) {
      simcData = append(simcData, 'talents', talents);
    }

    // apply role if a healer
    const charLoadout = activeLoadout(character);
    charSpec = _.get(charLoadout, 'talents.specName');
  }

  // append upgraded equipped items for upgrade Droptimizer sims
  if (type === 'droptimizer' && droptimizer.upgradeLevel) {
    simcData = appendEmptyLine(simcData);
    simcData = appendComment(simcData, ' Upgraded Equipped Items');
    _.each(droptimizer.equipped, (item, slot) => {
      if (item.upgradeOrig && item.upgrade.level !== item.upgradeOrig.level) {
        const upgradeName = `${item.upgrade.name} ${item.upgrade.level}/${item.upgrade.max}`;
        simcData = appendComment(simcData, ` ${item.name} (${item.itemLevel}) ${upgradeName}`);
        simcData = append(simcData, simcSlot(slot), `,${itemToString(item)}`);
      }
    });
    simcData = appendEmptyLine(simcData);
  }

  const specInfo = getSpecInfo(charClass, charSpec);

  if (!find(simcData, 'role') && specInfo?.role === 'healer') {
    simcData = append(simcData, 'role', 'attack');
  }

  if (ptr) {
    simcData = prepend(simcData, 'ptr', 1);
  }

  primaryActor = primaryActor && primaryActor.replace(/,/g, '');
  simcData = append(simcData, 'name', primaryActor);

  simcData = appendEmptyLine(simcData);

  // true most of the time
  let singleActorBatch = true;
  let profilesetMetric;
  if (isTank(character)) {
    singleActorBatch = false;
  } else if (specInfo?.name === 'Augmentation') {
    singleActorBatch = false;
    profilesetMetric = 'raid_dps';
  }

  let aplData;
  if (apl) {
    aplData = parse(apl);
  }

  simcData = appendComment(simcData, ' Consumables');

  const consumables = ['potion', 'food', 'flask', 'augmentation'];
  _.each(consumables, consumable => {
    // check custom APL first. If in custom APL, don't use global options
    const inCustomApl = Boolean(find(aplData, consumable));
    if (!inCustomApl) {
      // check for global override
      const finalValue = hasGet(overrides, consumable, formData[consumable]);
      if (finalValue) {
        simcData = append(simcData, consumable, finalValue);
      }
    }
  });

  //////////////////////////////////////////////////////////////////////////////
  // Expansion Options
  //////////////////////////////////////////////////////////////////////////////

  simcData = appendEmptyLine(simcData);

  simcData = appendComment(simcData, ' Expansion Options');

  // automatically resolve expansion options looking at incoming data, fight overrides, or custom APL
  _.each(expansionOptions, option => {
    if (!option.disabled) {
      const { key, simcOption } = option;
      const aplValue = find(aplData, simcOption);
      const override = _.get(overrides, key);
      const formValue = formData[key];

      let rawValue;
      if (aplValue) {
        // if APL has defined this, do not output in expansion options sectione
        rawValue = null;
      } else if (override !== undefined) {
        rawValue = override;
      } else if (formValue !== undefined) {
        rawValue = formValue;
      } else {
        rawValue = option.default;
      }

      let value;
      if (_.isUndefined(rawValue)) {
        value = null;
      } else if (_.isBoolean(rawValue)) {
        value = rawValue ? 1 : 0;
      } else {
        value = rawValue;
      }

      if (option.simcTransform) {
        value = option.simcTransform(value);
      }

      if (value !== null) {
        if (_.isArray(value)) {
          simcData = appendMany(simcData, value);
        } else {
          simcData = append(simcData, simcOption, value);
        }
      }
    }
  });

  if (powerInfusion || overrides?.powerInfusion) {
    simcData = appendEmptyLine(simcData);
    simcData = append(simcData, 'external_buffs.pool', 'power_infusion:120');
  }
  // ['external_buffs.pool', hasGet(overrides, 'powerInfusion', powerInfusion ? 'power_infusion:120' : '')],

  if (overrides?.customInput) {
    simcData = appendEmptyLine(simcData);
    _.each(overrides.customInput, ([key, value]) => {
      simcData = append(simcData, key, value);
    });
  }

  if (_.size(aplData) > 0) {
    simcData = appendComment(simcData, ' Custom APL');
    simcData = appendEmptyLine(simcData);
    _.each(aplData, item => simcData.push(item));
    simcData = appendEmptyLine(simcData);
  }

  simcData = appendEmptyLine(simcData);

  if (expertPreActors) {
    const preActorsData = parse(expertPreActors);
    simcData = appendComment(simcData, ' SimC Expert Mode: Pre-Actors');
    _.each(preActorsData, item => simcData.push(item));
    simcData = appendEmptyLine(simcData);
  }

  if (expertRaidActors) {
    const raidActorsData = parse(expertRaidActors);
    simcData = appendComment(simcData, ' SimC Expert Mode: Raid Actors');
    _.each(raidActorsData, item => simcData.push(item));
    simcData = appendEmptyLine(simcData);

    // this expert field is a clear signal we want a raid sim so treat all copies as part of the raid for
    // aug sims
    singleActorBatch = false;
  }

  simcData = appendComment(simcData, ' Actors');

  const actors = [];

  //////////////////////////////////////////////////////////////////////////////
  //
  // Gear Compare
  //
  //////////////////////////////////////////////////////////////////////////////

  // new gear compare with gearsets
  if (type === 'gear' && gearsets) {
    gearsets.forEach(gearset => {
      // simc actors can't have a comma in the name
      let title;
      if (gearset.name) {
        title = gearset.name.replace(/[,".]/g, '');
      } else if (gearset.defaultName) {
        title = gearset.defaultName;
      } else {
        title = 'Gearset';
      }

      const actor = {
        title,
        data: {},
      };

      // item commands
      gearset.items.forEach((rawItem) => {
        const {
          item,
          itemSlot,
          itemLevel,
          itemGem, // legacy
          itemGem0,
          itemGemLevel0,
          itemGem1,
          itemGemLevel1,
          itemGem2,
          itemGemLevel2,
          itemEnchant,
          itemSecondary,
          itemAffix,
          itemHand,
          craftedStats,
          embellishment,
        } = rawItem;
        if (item && item.name) {
          const slotName = itemSlot || itemHand || defaultSlot(item);
          let gems;
          let gemId;
          let gemLevels;
          let addSockets = false;
          if (Number.isNaN(Number(itemGem0))) {
            gems = [itemGem0, itemGem1, itemGem2].filter(g => g).join('_') || itemGem;
          } else {
            gemId = _.dropRightWhile(_.map([itemGem0, itemGem1, itemGem2], Number), g => !g).join('/');
            gemLevels = _.dropRightWhile(
              _.map([itemGemLevel0, itemGemLevel1, itemGemLevel2], Number),
              g => !g,
            ).join('/');
          }
          if (gemId) {
            addSockets = _.size(_.filter(_.split(gemId, '/'), gem => {
              const gemInfo = getGemDetails(gem);
              if (gemInfo) {
                return gemInfo.type === SOCKET_TYPES.PRISMATIC;
              }
              return false;
            }));
          }
          let craftedStatsStr;
          if (craftedStats) {
            craftedStatsStr = _.get(_.find(CRAFTED_STAT_COMBOS, { value: craftedStats }), 'id');
          }
          let enchantId;
          if (itemEnchant) {
            enchantId = getEnchantByTokenizedName(itemEnchant)?.id;
          }
          let embellishmentBonusIds = [];
          if (embellishment) {
            const embelInfo = getEmbellishment(embellishment);
            embellishmentBonusIds = embelInfo.craftingBonusIds;
          }
          const itemValues = {
            id: item.id,
            ilevel: itemLevel,
            gems,
            gem_id: gemId,
            gem_ilevel: gemLevels,
            enchant_id: enchantId,
            bonus_id: _.filter([
              addSockets ? _.findKey(GameData.BonusSockets, s => s === addSockets) : null,
              itemSecondary,
              itemAffix,
              ...embellishmentBonusIds,
            ]).join('/'),
            crafted_stats: craftedStatsStr,
          };
          const itemStr = _.map(
            _.pickBy(itemValues), // remove empty values
            (val, key) => `,${key}=${val}`,
          ).join('');

          actor.data[slotName] = itemStr;

          if (slotName === 'main_hand') {
            const mainHandiness = getWeaponHands(item);
            if (mainHandiness === '2h' && specInfo && !specInfo.dualWield2h) {
              actor.data.off_hand = ',';
            }
          }
        }
      });

      actors.push(actor);
    });
  }

  //////////////////////////////////////////////////////////////////////////////
  //
  // Talent Compare
  //
  //////////////////////////////////////////////////////////////////////////////

  if (type === 'talents' && talentSets) {
    _.each(talentSets, talentSet => {
      const title = talentSet.name || talentSet.talents;

      const actor = {
        title,
        data: {},
      };

      actor.data.talents = talentSet.talents;
      actors.push(actor);
    });
  }

  // Tanks and enemies are funny - using copy results in weird DTPS scenarios that muck things up
  // So always use psets for consistency with Top Gear / Droptimizer for tanks
  // (and maybe just switch over to this for everyone eventually)
  //
  // Also use psets of profilesetMetric is used - this will make aug sims use psets in gear compare
  const usePsets = isTank(character) || profilesetMetric;

  const titlesUsed = new Set();
  _.each(actors, actor => {
    const baseTitle = actor.title;

    // make sure we have a unique copy/pset name from the title
    let title = baseTitle;
    let titleNum = 1;
    while (titlesUsed.has(title)) {
      titleNum += 1;
      title = `${baseTitle} (${titleNum})`;
    }
    titlesUsed.add(title);

    if (!usePsets) {
      simcData = append(simcData, 'copy', `${title},${primaryActor}`);
    }

    _.each(actor.data, (value, key) => {
      if (usePsets) {
        simcData = profilesetAppend(simcData, title, key, value);
      } else {
        simcData = append(simcData, key, value);
      }
    });

    simcData = appendEmptyLine(simcData);
  });

  //////////////////////////////////////////////////////////////////////////////
  //
  // Droptimizer
  //
  //////////////////////////////////////////////////////////////////////////////

  if (type === 'droptimizer') {
    const equippedWeaponHands = getWeaponHands(droptimizer.equipped.mainHand);

    const items = _.reject(droptimizerItems, i => i.invalidReason);

    _.each(items, ({ id, slot, item, alternate, alternateSlot }) => {
      const hands = getWeaponHands(item);

      // skip variations that would result in 2h + oh
      if (specInfo && !specInfo.dualWield2h && equippedWeaponHands === '2h') {
        if (droptimizer.mainHandAlternate === null || droptimizer.offHandAlternate === null) {
          return;
        }
      }

      const itemStr = itemToString(item);

      if (verbose) {
        const commentParts = _.filter([
          `${item.name} ${item.itemLevel}`,
          _.get(item, 'instance.name'),
          _.get(item, 'encounter.name'),
        ]);
        const comment = _.join(commentParts, ' - ');
        simcData = appendComment(simcData, ` ${comment}`);
      }
      simcData = profilesetAppend(simcData, id, slot, `,${itemStr}`);
      // remove off hand for casters
      if (specInfo && !specInfo.dualWield2h) {
        if (hands === '2h') {
          simcData = profilesetAppend(simcData, id, 'off_hand', ',');
        }
      }
      if (alternateSlot) {
        simcData = profilesetAppend(simcData, id, alternateSlot, `,${itemToString(alternate)}`);
      }
      simcData = appendEmptyLine(simcData);
    });
  }

  //////////////////////////////////////////////////////////////////////////////
  //
  // Top Gear combinations
  //
  //////////////////////////////////////////////////////////////////////////////

  /* eslint-disable no-plusplus */
  if (type === 'optimize') {
    /*
     * Large arrays are slow when using immutable operations.
     * Generating the simc input for combos is O(n^2) which causes tons of garbage collection.
     * For this tight loop, manually allocate and build the array using fast array operations.
     */
    const data = new Array(optimizeCombos.length * 20);
    let dataEnd = 0;

    const combosLen = optimizeCombos.length;

    for (let comboNum = 1; comboNum < combosLen; comboNum += 1) {
      dataEnd = addOptimizeActor(data, optimizeCombos, comboNum, simMode, primaryActor, dataEnd, verbose);
    }

    data[dataEnd++] = { emptyLine: true };

    simcData = simcData.concat(data);
  }
  /* eslint-enable no-plusplus */

  simcData = appendEmptyLine(simcData);

  //////////////////////////////////////////////////////////////////////////////
  // Post-actors, enemies
  //////////////////////////////////////////////////////////////////////////////

  if (expertPostActors) {
    const postActorsData = parse(expertPostActors);
    simcData = appendComment(simcData, ' SimC Expert Mode: Post-Actors');
    _.each(postActorsData, item => simcData.push(item));
    simcData = appendEmptyLine(simcData);
  }

  const enemies = _.get(overrides, 'enemies');
  if (enemies) {
    simcData = appendComment(simcData, ' Custom Enemies');
    _.each(enemies, enemy => {
      simcData = append(simcData, 'enemy', enemy.name);
      _.each(enemy.properties, (value, key) => {
        simcData = append(simcData, key, value);
      });
      simcData = appendEmptyLine(simcData);
    });
  }

  //////////////////////////////////////////////////////////////////////////////
  // Simulation options, iterations, footer stuff
  //////////////////////////////////////////////////////////////////////////////

  // set a bunch of stuff here. these will override user provided lines from
  // simc input

  simcData = appendComment(simcData, ' Simulation Options');

  const iters = iterations === 'smart' ? 100000 : iterations;
  let targetError;
  if (isSmartIterations(iterations)) {
    targetError = smartHighPrecision
      ? SharedConfig.flightmaster.highPrecision
      : SharedConfig.flightmaster.lowPrecision;
  } else {
    targetError = iters > 25000 ? SharedConfig.flightmaster.highPrecision : 0;
  }

  let scaleStats = [
    'strength',
    'intellect',
    'agility',
    'crit',
    'mastery',
    'vers',
    'haste',
    'weapon_dps',
    'weapon_offhand_dps',
  ];
  if (specInfo && specInfo.extraStatScales) {
    scaleStats = [...scaleStats, ...specInfo.extraStatScales];
  }

  let highDetail = reportDetails;
  if (type === 'quick') {
    highDetail = true;
  }

  const fightStyleToUse = hasGet(overrides, 'fightStyle', fightStyle);

  if (fightStyleToUse && _.toLower(fightStyleToUse) !== 'patchwerk') {
    simcData = appendMany(simcData, [
      ['fight_style', fightStyleToUse],
    ]);
  }

  simcData = appendMany(simcData, [
    // overwrite iterations to a reasonable value
    ['iterations', iters],
    ['desired_targets', hasGet(overrides, 'enemyCount', enemyCount)],
    ['max_time', hasGet(overrides, 'fightLength', fightLength)],

    // overwrite scale factors based on incoming data
    ['calculate_scale_factors', type === 'stats' ? 1 : 0],

    ['scale_only', scaleStats.join(',')],

    ['override.bloodlust', hasGet(overrides, 'bloodlust', bloodlust ? '1' : '0')],
    ['override.arcane_intellect', hasGet(overrides, 'arcaneIntellect', arcaneIntellect ? '1' : '0')],
    ['override.power_word_fortitude', hasGet(overrides, 'fortitude', fortitude ? '1' : '0')],
    ['override.battle_shout', hasGet(overrides, 'battleShout', battleShout ? '1' : '0')],
    ['override.mystic_touch', hasGet(overrides, 'mysticTouch', mysticTouch ? '1' : '0')],
    ['override.chaos_brand', hasGet(overrides, 'chaosBrand', chaosBrand ? '1' : '0')],
    ['override.skyfury', hasGet(overrides, 'skyfury', skyfury ? '1' : '0')],
    ['override.mark_of_the_wild', hasGet(overrides, 'markOfTheWild', markOfTheWild ? '1' : '0')],
    ['override.hunters_mark', hasGet(overrides, 'huntersMark', huntersMark ? '1' : '0')],
    ['override.bleeding', hasGet(overrides, 'bleeding', bleeding ? '1' : '0')],
  ]);

  simcData = appendMany(simcData, [
    ['report_details', highDetail ? '1' : '1'],
    ['single_actor_batch', singleActorBatch ? '1' : '0'],
    ['optimize_expressions', 1],
  ]);

  if (profilesetMetric) {
    simcData = append(simcData, 'profileset_metric', profilesetMetric);
  }

  if (targetError) {
    simcData = append(simcData, 'target_error', targetError);
  }

  if (expertFooter) {
    simcData = appendEmptyLine(simcData);
    const footerData = parse(expertFooter);
    simcData = appendComment(simcData, ' SimC Expert Mode: Footer');
    _.each(footerData, item => simcData.push(item));
  }

  return simcData;
}

function fromCharacter(character, region) {
  if (!character) {
    return null;
  }

  if (character.level < MAX_LEVEL) {
    return null;
  }

  let data = [];
  const warnings = [];
  const notices = [];

  const loadout = activeLoadout(character);
  const specInfo = getSpecInfo(loadout.talents?.classId, loadout.talents?.specId);

  const className = _.replace(_.toLower(specInfo?.class.name), /\s/g, '');
  const specName = _.replace(_.toLower(specInfo?.name), /\s/g, '_');
  const raceName = _.replace(
    _.replace(
      _.toLower(_.find(races, { id: character.race }).name),
      /\s/g,
      '_',
    ),
    /[^a-z_ ]/gi,
    '',
  );

  const realm = findRealm(region, character.realm);

  // warnings
  if (raceName === 'zandalari_troll') {
    warnings.push('Armory does not include info about your Zandalari Loa, SimC input will use the default (Pa\'ku)');
  }

  const v2SlotMap = {
    back: 'BACK',
    chest: 'CHEST',
    feet: 'FEET',
    finger1: 'FINGER_1',
    finger2: 'FINGER_2',
    hands: 'HANDS',
    head: 'HEAD',
    legs: 'LEGS',
    mainHand: 'MAIN_HAND',
    neck: 'NECK',
    offHand: 'OFF_HAND',
    shirt: 'SHIRT',
    shoulder: 'SHOULDER',
    tabard: 'TABARD',
    trinket1: 'TRINKET_1',
    trinket2: 'TRINKET_2',
    waist: 'WAIST',
    wrist: 'WRIST',
  };

  const items = [];

  _.each(_.pickBy(character.items, _.isObject), (item, slot) => {
    // determine is bonus IDs and item level match

    // find missing bonus IDs
    const bonuses = [...item.bonusLists];

    // find item from v2 equipment
    const v2item = _.find(
      _.get(character, 'v2.equipment.equipped_items'),
      {
        slot: {
          type: v2SlotMap[slot],
        },
      },
    );

    const attrs = {
      id: item.id,
      bonus_id: _.join(bonuses, '/'),
      gem_id: _.join(
        _.map(
          _.pickBy(item.tooltipParams, (value, key) => key.match(/^gem\d/)),
          gemId => gemId,
        ),
        '/',
      ),
      enchant_id: item.tooltipParams.enchant,
      drop_level: _.get(v2item, 'timewalker_level'),
      context: _.get(v2item, 'context'),
      crafted_stats: _.join(_.map(_.get(v2item, 'modified_crafting_stat'), 'id'), '/'),
    };
    const attrsStr = _.join(_.map(_.pickBy(attrs), (value, key) => `${key}=${value}`), ',');
    items.push({
      slot: simcSlot(slot),
      value: `,${attrsStr}`,
    });
  });

  data = appendComment(
    data,
    ` Raidbots-generated SimC input for Armory character ${_.toUpper(region)} ${character.realm} ${character.name}`,
  );
  data = appendComment(data, ` On ${new Date().toISOString()}`);

  if (warnings.length > 0) {
    data = appendEmptyLine(data);
    _.each(warnings, warning => {
      data = appendComment(data, ` Warning: ${warning}`);
    });
  }
  if (notices.length > 0) {
    data = appendEmptyLine(data);
    _.each(notices, notice => {
      data = appendComment(data, ` Info: ${notice}`);
    });
  }

  data = appendEmptyLine(data);

  data = setMany(
    data,
    [
      [className, character.name],
      ['level', character.level],
      ['race', raceName],
      ['region', region],
      ['server', _.snakeCase(realm.slug)],
      ['role', specInfo?.role === 'healer' ? 'attack' : specInfo?.role],
      ['spec', specName],
      ['talents', TalentTraits.stringify(loadout.talents)],
    ],
  );

  data = appendEmptyLine(data);

  _.each(items, ({ value, slot }) => {
    data = append(data, slot, value);
  });

  return data;
}

// partial transform simc input to armory character data
function toCharacter(data) {
  // find first actor
  const actors = getActors(data);
  if (!actors || actors.length === 0) {
    return null;
  }
  const primaryActor = actors[0];
  const actorClass = _.find(classes, clazz => (
    primaryActor.key === clazz.name.toLowerCase().replace(/ /g, '')
  ));
  const simcRace = find(data, 'race')?.toLowerCase();
  const actorRace = _.find(races, { simcName: simcRace });
  let faction;
  if (actorRace) {
    faction = _.find(factions, { name: actorRace.side });
  }

  const spec = find(data, 'spec');
  const lootSpec = find(data, 'loot_spec', true);
  const role = specMap[spec];

  const classId = _.get(actorClass, 'id');
  const specInfo = getSpecInfo(classId, spec);
  const lootSpecInfo = getSpecInfo(classId, lootSpec);

  // items
  const items = {};
  _.each(COMMANDS_GEAR, gearSlot => {
    const item = find(data, gearSlot);
    if (item) {
      const attributes = _.fromPairs(item.split(',').map(attr => attr.split('=')));
      const gems = _.split(attributes.gem_id, '/');
      // ItemCache is meant to get core item info (icon, base level, etc).
      // There can be issues if a character has two different versions of the same item equipped (e.g. mythic main
      // hand but heroic off hand). I've tried to reset as much info as possible but cache bonus IDs / item level can
      // maybe be a problem in some cases
      const cacheItem = ItemCache.get(attributes.id);
      const it = {
        ...cacheItem,
        id: attributes.id,
        tooltipParams: {
          enchant: attributes.enchant_id,
          gem0: gems[0],
          gem1: gems[1],
          gem2: gems[2],
        },
        bonusLists: attributes.bonus_id ? attributes.bonus_id.split('/') : null,
        bonus_id: attributes.bonus_id,
        craftedStats: attributes.crafted_stats ? attributes.crafted_stats.split('/') : undefined,
        enchant_id: attributes.enchant_id,
        gem_id: attributes.gem_id,
        itemLevel: null,
      };

      items[_.camelCase(gearSlot)] = it;
    }
  });

  const talentLoadouts = getTalentLoadouts(data);

  const catalystCurrencies = _.map(
    _.filter(_.split(find(data, 'catalyst_currencies', true), '/')),
    currencyStr => {
      const [id, amount] = _.split(currencyStr, ':');
      return {
        id: parseInt(id, 10),
        amount: parseInt(amount, 10),
      };
    },
  );

  const currencyTypes = {
    c: 'currency',
    i: 'item',
  };
  const upgradeCurrencies = _.map(
    _.filter(_.split(find(data, 'upgrade_currencies', true), '/')),
    currencyStr => {
      const [type, id, amount] = _.split(currencyStr, ':');
      return {
        type: currencyTypes[type],
        id: parseInt(id, 10),
        amount: parseInt(amount, 10),
      };
    },
  );

  const slotHighWatermarks = _.map(
    _.filter(_.split(find(data, 'slot_high_watermarks', true), '/')),
    str => {
      const [slot, charWatermark, accountWatermark] = _.map(_.split(str, ':'), i => parseInt(i, 10));
      return {
        slot,
        character: charWatermark,
        account: accountWatermark,
      };
    },
  );

  const upgradeAchievements = _.map(
    _.filter(_.split(find(data, 'upgrade_achievements', true), '/')),
    i => parseInt(i, 10),
  );

  const character = {
    name: primaryActor.value,
    region: find(data, 'region'),
    realm: find(data, 'server'),
    class: actorClass ? actorClass.id : 'unknown',
    level: parseInt(find(data, 'level'), 10),
    race: actorRace ? actorRace.id : null,
    faction: faction ? faction.id : 1,
    items,
    talents: [{
      selected: true,
      spec: {
        id: _.get(specInfo, 'id'),
        name: _.startCase(spec),
        role,
      },
    }],
    lootSpec: lootSpec && {
      id: _.get(lootSpecInfo, 'id'),
      name: _.startCase(lootSpec),
    },
    talentLoadouts,
    catalystCurrencies,
    upgradeCurrencies,
    slotHighWatermarks,
    upgradeAchievements,
  };

  return character;
}

function getTotalIterations(data, options = {}) {
  // 1 x primary, 4 x secondary, weapon DPS, (AP or SP)
  const DEFAULT_SCALE_STATS_NUM = 7;
  const DEFAULT_DPS_PLOT_POINTS = 20;
  const DEFAULT_REFORGE_PLOT_AMOUNT = 200;
  const DEFAULT_REFORGE_PLOT_STEP = 20;

  const NON_SMART_MIN_ITERATIONS = 25000;

  const { defaultIterations, overrideIterations } = options;

  // get iterations
  let minIterations = 5000;
  const iterations = overrideIterations || int(find(data, 'iterations')) || getIterations(defaultIterations);
  const scaleFactors = int(find(data, 'calculate_scale_factors'));
  const scaleOnly = find(data, 'scale_only');
  const centerScaleDelta = int(find(data, 'center_scale_delta'));
  const dpsPlotStat = find(data, 'dps_plot_stat');
  const dpsPlotPoints = int(find(data, 'dps_plot_points') || DEFAULT_DPS_PLOT_POINTS);
  const reforgePlotStat = find(data, 'reforge_plot_stat');
  const reforgePlotAmount = find(data, 'reforge_plot_amount') || DEFAULT_REFORGE_PLOT_AMOUNT;
  const reforgePlotStep = find(data, 'reforge_plot_step') || DEFAULT_REFORGE_PLOT_STEP;

  let numActors = 1; // assume armory with no copies
  const actors = getActors(data);
  if (actors) {
    numActors = actors.length;
  }

  // also add distinct profilesets
  const profilesets = getProfilesets(data);
  const numProfilesets = _.keys(profilesets).length;

  // dps: base + main stat + 4 secondaries
  // tanks just sim DPS so the same as DPS role
  let numBatches = 1;
  if (scaleFactors) {
    let numScaleStats = DEFAULT_SCALE_STATS_NUM;
    if (scaleOnly) {
      // if multiple primaries listed, only count them once
      const scaleStats = _.uniq(
        _.map(
          _.split(scaleOnly, ','),
          s => _.replace(s, /^(strength|intellect|agility)$/i, 'primary'),
        ),
      );
      numScaleStats = scaleStats.length;
    }
    numBatches += centerScaleDelta ? numScaleStats * 2 : numScaleStats;
    minIterations = NON_SMART_MIN_ITERATIONS;
  }

  // check for plotting
  if (dpsPlotStat) {
    const numStats = _.split(dpsPlotStat, ',').length;
    // plotting makes the same number of positive and negative samples away from the baseline
    // for odd dps_plot_points, it runs ((dps_plot_points - 1) / 2) on each side
    const nonBaseLineBatches = dpsPlotPoints % 2 === 0 ? dpsPlotPoints : dpsPlotPoints - 1;
    // add the baseline to the total stat adjustments
    numBatches += 1 + (numStats * nonBaseLineBatches);
    minIterations = NON_SMART_MIN_ITERATIONS;
  }

  // check for reforging
  if (reforgePlotStat) {
    const numStats = _.split(reforgePlotStat, ',').length;
    const numSteps = reforgePlotAmount / reforgePlotStep;

    /*
      2 stats, amount 20, step 10 (2 steps) = 5 batches
      base, -20/20, -10/10, 0/0, 10/-10, 20/-20

      2 stats, amount 50, step 10 (5 steps) = 11 batches
      -50/50, -40/40, -30/30, -20/20, -10/10, 0/0, 10/-10, 20/-20, 30/-30, 40/-40, 50/-50

      2 stats, 500, 200 = 11 points
      3 stats, 500, 200 = 55 points


      3 stats, 10 amount, 10 step (3 steps)
      7 (2 + 3 + 2)

      3 stats, 20 amount, 10 step (5 steps)
      19 (3 + 4 + 5 + 4 + 3)

      3 stats, 30 amount, 10 step (7 steps)
      37 (4 + 5 + 6 + 7 + 6 + 5 + 4)
     */

    // This works for n == 2 and is OKish for higher dimensions
    let reforgeBatches = 1 + ((2 * numSteps) ** (numStats - 1));

    // these insane formula are from Norrinir tinkering around
    // https://discord.com/channels/244067101478879234/244072280919900160/806375342880325683
    if (numStats === 3) {
      reforgeBatches = (3 * (numSteps ** 2)) + (3 * numSteps) + 1;
    } else if (numStats === 4) {
      reforgeBatches = ((16 / 3) * (numSteps ** 3)) + (8 * (numSteps ** 2)) + ((14 / 3) * numSteps) + 1;
    }
    // this is an estimate, sometimes it's too low, sometimes too high, but generally within an order-of-magnitude
    numBatches += reforgeBatches;
    minIterations = NON_SMART_MIN_ITERATIONS;
  }


  let profilesetIterations = 0;
  if (numProfilesets > 0) {
    profilesetIterations = _.reduce(
      profilesets,
      (result, pset) => {
        let psetIterations = iterations;
        _.each(pset, psetLine => {
          const command = getProfilesetCommand(psetLine);
          if (command && command.key === 'iterations') {
            psetIterations = parseInt(_.get(command, 'value')) || iterations;
          }
        });
        return result + (numActors * psetIterations);
      },
      0,
    );
  }

  const finalIterations = _.max([iterations, minIterations]);

  const total = (numActors * numBatches * finalIterations) + profilesetIterations;
  return total;
}

function splitProfilesetSim(data, chunkSize) {
  const results = [];

  // get base options without profilesets
  const baseData = getNonProfilesetCommands(data);

  const profilesets = _.map(getProfilesets(data), pset => pset);

  if (profilesets.length === 0) {
    return [
      [...baseData],
    ];
  }

  const chunks = _.chunk(profilesets, chunkSize);

  _.each(chunks, chunk => {
    const chunkData = [...baseData];
    _.each(chunk, profileset => {
      _.each(profileset, command => chunkData.push(command));
    });
    results.push(chunkData);
  });

  return results;
}

function convertCopyToProfilesets(input) {
  const data = parse(input);

  // first pass - collect base info and copy dependencies
  const actorTree = [{
    name: null,
    parent: null,
    commands: [],
    base: true,
  }];
  const baseActor = actorTree[0];
  let currentActor = baseActor;
  _.each(data, command => {
    if (isEmptyLine(command) || isComment(command)) {
      return;
    }
    if (!currentActor && command.key === 'name') {
      baseActor.name = command.value;
    } else if (command.key === 'copy') {
      const [copyName, copyParent] = _.split(command.value, ',');

      const name = _.trim(copyName, '"');
      const parentName = _.trim(copyParent || currentActor.name, '"');
      const commands = _.cloneDeep(_.get(_.find(actorTree, { name: parentName }), 'commands', []));

      const newActor = {
        name,
        parent: parentName,
        commands,
      };
      currentActor = newActor;
      actorTree.push(currentActor);
    } else if (!currentActor.base && _.includes(COMMANDS_PLAYER_SCOPE, command.key)) {
      const existingCommandIdx = _.findIndex(currentActor.commands, { key: command.key });
      if (existingCommandIdx >= 0) {
        // replace option
        currentActor.commands[existingCommandIdx] = command;
      } else {
        // add option
        currentActor.commands.push(command);
      }
    } else {
      baseActor.commands.push(command);
    }
  });

  const newData = [];
  _.each(actorTree, actor => {
    if (!actor.base) {
      newData.push({ emptyLine: true });
    }
    _.each(actor.commands, command => {
      if (actor.base) {
        newData.push(command);
      } else {
        newData.push({
          key: `profileset."${actor.name}"`,
          value: `${command.key}=${command.value}`,
          op: '+=',
        });
      }
    });
  });

  return stringify(newData);
}

function isRaidSim(data) {
  const numActors = getActors(data)?.length || 1;
  const numProfilesets = _.keys(getProfilesets(data)).length;
  const metric = find(data, 'profileset_metric');

  if (metric === 'raid_dps') {
    return true;
  }

  return numActors > 1 && numProfilesets > 0;
}

export default {
  parse,
  stringify,
  fromCharacter,
  toCharacter,
  prepend,
  reject,
  append,
  appendMany,
  prependEmptyLine,
  appendEmptyLine,
  removeEmptyLines,
  prependComment,
  appendComment,
  removeComments,
  appendAfter,
  set,
  impureSet,
  setMany,
  profilesetAppend,
  impureProfilesetAppend,
  profilesetRemove,
  removeProfilesets,
  impureRemoveProfilesets,
  find,
  getComments,
  getValidCommands,
  getInvalidCommands,
  getInvalidAdvancedCommands,
  getInvalidAplCommands,
  getActors,
  getProfilesets,
  getProfilesetCommands,
  getProfilesetNames,
  getGear,
  getRequiredGear,
  getGearFromComments,
  getTalentLoadouts,
  isCompleteGearSet,
  getCharacter,
  formInputToData,
  getTotalIterations,
  splitProfilesetSim,
  convertCopyToProfilesets,
  isRaidSim,
};
